import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type { PipeStatusRecord, ProjectStatus } from "@/types"

type Props = {
  value: string
  onChange: (v: string) => void
  status: ProjectStatus
  pipeStatuses: PipeStatusRecord[]
}

export function PipeStatusCombobox({ value, onChange, status, pipeStatuses }: Props) {
  const [open, setOpen] = useState(false)
  const options = pipeStatuses.filter((ps) => ps.parent_status === status)
  const selected = options.find((o) => o.id === value)

  if (options.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-[38px] w-[220px] justify-between font-normal"
        >
          {selected ? (
            <span
              className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
              style={{ backgroundColor: selected.bg_color, color: selected.text_color }}
            >
              {selected.label}
            </span>
          ) : (
            <span className="text-muted-foreground">Statut...</span>
          )}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher..." />
          <CommandList>
            <CommandEmpty>Aucun résultat</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all"
                onSelect={() => {
                  onChange("all")
                  setOpen(false)
                }}
              >
                <Check className={cn("mr-2 size-4", !value ? "opacity-100" : "opacity-0")} />
                Tous les statuts
              </CommandItem>
              {options.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.id)
                    setOpen(false)
                  }}
                >
                  <Check className={cn("mr-2 size-4", value === opt.id ? "opacity-100" : "opacity-0")} />
                  <span
                    className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: opt.bg_color, color: opt.text_color }}
                  >
                    {opt.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
